import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Auth({ mode }) {
  const isSignup = mode === "signup";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { setUser } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const url = isSignup ? "/auth/register" : "/auth/login";
      const body = isSignup ? { email, password, name } : { email, password };
      const { data } = await api.post(url, body);
      setUser(data);
      toast.success(isSignup ? "Welcome to your quiet space." : "Welcome back.");
      nav("/app/checkin");
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-5 py-10">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-3 mb-10" data-testid="auth-brand-link">
          <span className="leaf-mark" />
          <span className="font-serif-display text-2xl">Meridia</span>
        </Link>

        <div className="card-paper p-8 sm:p-10 paper-grain rise">
          <h1 className="font-serif-display text-3xl text-[#2C221E] leading-tight" data-testid="auth-title">
            {isSignup ? "Begin your journal" : "Welcome back"}
          </h1>
          <p className="mt-3 text-[#73635B]">
            {isSignup
              ? "A private place for the nights, the fog, the fire."
              : "Your journal has been waiting."}
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            {isSignup && (
              <div>
                <label className="text-xs uppercase tracking-widest text-[#8C7A70] font-semibold">
                  Name (optional)
                </label>
                <input
                  type="text"
                  className="input-paper mt-2"
                  placeholder="What should Meridia call you?"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="auth-name-input"
                />
              </div>
            )}
            <div>
              <label className="text-xs uppercase tracking-widest text-[#8C7A70] font-semibold">
                Email
              </label>
              <input
                type="email"
                className="input-paper mt-2"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="auth-email-input"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-[#8C7A70] font-semibold">
                Password
              </label>
              <input
                type="password"
                className="input-paper mt-2"
                placeholder="At least 8 characters"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                data-testid="auth-password-input"
              />
            </div>
            {error && (
              <p className="text-sm text-[#C26D53]" data-testid="auth-error">
                {error}
              </p>
            )}
            <button
              type="submit"
              className="btn-primary w-full"
              disabled={loading}
              data-testid="auth-submit-button"
            >
              {loading ? "One moment…" : isSignup ? "Create account" : "Sign in"}
            </button>
          </form>

          <div className="mt-6 text-sm text-[#52443E] flex items-center justify-between">
            <div>
              {isSignup ? (
                <>Already here? <Link to="/signin" className="text-[#C26D53] hover:underline" data-testid="auth-switch-signin">Sign in</Link></>
              ) : (
                <>New to Meridia? <Link to="/signup" className="text-[#C26D53] hover:underline" data-testid="auth-switch-signup">Create an account</Link></>
              )}
            </div>
            {!isSignup && (
              <Link to="/forgot-password" className="text-[#8C7A70] hover:text-[#C26D53]" data-testid="auth-forgot-link">
                Forgot?
              </Link>
            )}
          </div>
        </div>

        <p className="mt-6 text-xs text-[#8C7A70] text-center">
          Your entries are private. Meridia never shares them.
        </p>
      </div>
    </div>
  );
}
